import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  LinearProgress,
  Box,
  IconButton,
  Menu,
  MenuItem
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Group as GroupIcon,
  Assignment as AssignmentIcon,
  PushPin as PushPinIcon,
  PushPinOutlined as PushPinOutlinedIcon,
  ChatBubbleOutline as ChatIcon
} from '@mui/icons-material';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import ProjectCountdown from './ProjectCountdown';
import ProjectChat from './ProjectChat';

function ProjectCard({ project, onEdit, onDelete, onDeletePermanently, onPin }) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [chatOpen, setChatOpen] = React.useState(false);
  const [currentUser, setCurrentUser] = React.useState(null);

  React.useEffect(() => {
    const user = JSON.parse(localStorage.getItem('era_user'));
    setCurrentUser(user);
  }, []);

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleChatOpen = (e) => {
    e.stopPropagation();
    setChatOpen(true);
  };

  const handleChatClose = (e) => {
    if (e) e.stopPropagation();
    setChatOpen(false);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    handleMenuClose();
    onEdit(project);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    handleMenuClose();
    onDelete(project);
  };

  const handleDeletePermanently = (e) => {
    e.stopPropagation();
    handleMenuClose();
    if (onDeletePermanently) {
      onDeletePermanently(project);
    }
  };

  const handlePin = (e) => {
    e.stopPropagation();
    if (onPin) {
      onPin(project);
    }
  };

  const handleCardClick = () => {
    navigate(`/projects/${project.id}`);
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'default',
      medium: 'primary',
      high: 'warning',
      urgent: 'error'
    };
    return colors[priority] || 'default';
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'success',
      completed: 'info',
      on_hold: 'warning',
      archived: 'default'
    };
    return colors[status] || 'default';
  };

  const completionRate = project.total_tasks > 0
    ? (project.completed_tasks / project.total_tasks) * 100
    : 0;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    
    // Handle ISO strings (e.g., 2026-02-02T23:00:00.000Z) or simple YYYY-MM-DD
    let cleanDateStr = dateStr;
    if (typeof dateStr === 'string' && dateStr.includes('T')) {
      cleanDateStr = dateStr.split('T')[0];
    }

    // Handle YYYY-MM-DD string directly to avoid timezone issues
    if (typeof cleanDateStr === 'string' && cleanDateStr.includes('-')) {
      const [year, month, day] = cleanDateStr.split('-');
      return `${day}/${month}/${year}`;
    }
    
    // Fallback for Date objects or other formats
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  return (
    <Card
      sx={{
        cursor: 'pointer',
        transition: 'all 0.3s',
        borderLeft: `4px solid ${project.color || '#3498db'}`,
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-4px)'
        }
      }}
      onClick={handleCardClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {project.title}
          </Typography>
          <Box>
            <IconButton
              size="small"
              onClick={handleChatOpen}
              sx={{ ml: 1, color: 'primary.main' }}
              title="Discussion du projet"
            >
              <ChatIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={handlePin}
              sx={{ 
                ml: 1,
                color: project.is_pinned ? 'primary.main' : 'action.disabled',
                transform: project.is_pinned ? 'rotate(0deg)' : 'rotate(45deg)',
                transition: 'all 0.2s'
              }}
            >
              {project.is_pinned ? <PushPinIcon /> : <PushPinOutlinedIcon />}
            </IconButton>
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              sx={{ ml: 0.5 }}
            >
              <MoreVertIcon />
            </IconButton>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleEdit}>Modifier</MenuItem>
            <MenuItem onClick={handleDelete} sx={{ color: 'warning.main' }}>
              Archiver
            </MenuItem>
            <MenuItem onClick={handleDeletePermanently} sx={{ color: 'error.main' }}>
              Supprimer définitivement
            </MenuItem>
          </Menu>
        </Box>

        {project.end_date && (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
            <ProjectCountdown 
              endDate={project.end_date} 
              startDate={project.start_date}
              status={project.status}
            />
          </Box>
        )}

        {/* Project Chat Dialog */}
        <Dialog
          open={chatOpen}
          onClose={handleChatClose}
          maxWidth="sm"
          fullWidth
          onClick={(e) => e.stopPropagation()}
        >
          <DialogContent sx={{ p: 0, height: '500px' }}>
            {currentUser && (
              <ProjectChat 
                projectId={project.id} 
                currentUserId={currentUser.id}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleChatClose}>Fermer</Button>
          </DialogActions>
        </Dialog>

        {project.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical'
            }}
          >
            {project.description}
          </Typography>
        )}

        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Chip
              label={project.priority}
              size="small"
              color={getPriorityColor(project.priority)}
            />
            <Chip
              label={project.status}
              size="small"
              color={getStatusColor(project.status)}
              variant="outlined"
            />
          </Box>
        </Box>

        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Progression
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {Math.round(completionRate)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={completionRate}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AssignmentIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              {project.completed_tasks || 0}/{project.total_tasks || 0} tâches
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <GroupIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              {project.team_members || 0} membres
            </Typography>
          </Box>
        </Box>

        {(project.start_date || project.end_date) && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              {project.start_date && `Début: ${formatDate(project.start_date)}`}
              {project.start_date && project.end_date && ' • '}
              {project.end_date && `Fin: ${formatDate(project.end_date)}`}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default ProjectCard;
